import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchMyActivations,
  fetchMyBatchCodes,
  fetchMyBatches,
  type VoucherActivationPage,
  type VoucherBatchPage,
  type VoucherCard,
} from './voucherStockApi'
import { voucherStockKeys } from './voucherStockKeys'

export interface BatchesState {
  page: VoucherBatchPage | null
  loading: boolean
  error: unknown
}

/**
 * Sherikka berilgan partiyalar.
 *
 * ⚠️ `retry: false` — endpoint bugun umuman yozilmagan
 * (`voucherStockApi.ts` fayl boshidagi o'lchov), ya'ni uch marta qayta
 * urinish har ochilishda uchta 404 berib, ekranni bir necha soniya
 * «yuklanmoqda» da ushlab turardi. `features/profile` dagi ayni qaror.
 */
export function useMyBatches(search: string, page: number): BatchesState {
  const query = useQuery({
    queryKey: voucherStockKeys.batches(search, page),
    queryFn: () => fetchMyBatches(search, page),
    retry: false,
  })

  return { page: query.data ?? null, loading: query.isPending, error: query.error }
}

export interface ActivationsState {
  page: VoucherActivationPage | null
  loading: boolean
  error: unknown
}

/** «Men ishlatgan kartalar» — davr chegarasi TENANT zonasida hisoblangan onlar */
export function useMyActivations(
  window: { from: string; to: string },
  page: number,
): ActivationsState {
  const query = useQuery({
    queryKey: voucherStockKeys.activations(window, page),
    queryFn: () => fetchMyActivations(window, page),
    retry: false,
  })

  return { page: query.data ?? null, loading: query.isPending, error: query.error }
}

export interface BatchCodesState {
  cards: readonly VoucherCard[]
  loading: boolean
  error: unknown
}

const NO_CARDS: readonly VoucherCard[] = []

/**
 * Partiyadagi kodlar — LOKAL HOLATDA, keshsiz.
 *
 * <h2>⛔ NEGA `useQuery` EMAS</h2>
 * Kod taqdim etuvchiga xizmat qiluvchi sir: uni ko'rgan odam kartani
 * o'zi ishlatib yuboradi. `useQuery` javobni `queryClient` ga qo'yadi va
 * u oyna YOPILGANDAN keyin ham (sukut bo'yicha besh daqiqa) xotirada,
 * React Query DevTools'da ochiq ko'rinadigan holda yashardi. Bu yerda
 * ro'yxat komponent holatida turadi va {@link reset} bilan yo'qoladi.
 *
 * ⚠️ Shu sababdan qayta ochilganda RO'YXAT QAYTA SO'RALADI. Bu ortiqcha
 * so'rov, lekin u ONGLI narx: keshning butun ma'nosi «saqlab qo'yish»,
 * sirning butun ma'nosi esa «saqlamaslik».
 */
export function useBatchCodes(): BatchCodesState & {
  load: (batchId: string) => void
  reset: () => void
} {
  const [cards, setCards] = useState<readonly VoucherCard[]>(NO_CARDS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const load = useCallback((batchId: string) => {
    setLoading(true)
    setError(null)
    setCards(NO_CARDS)
    fetchMyBatchCodes(batchId)
      .then((rows) => setCards(rows))
      .catch((cause: unknown) => setError(cause))
      .finally(() => setLoading(false))
  }, [])

  const reset = useCallback(() => {
    setCards(NO_CARDS)
    setError(null)
    setLoading(false)
  }, [])

  return { cards, loading, error, load, reset }
}
